import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import PaintDeliveryStatus from "@/components/PaintDeliveryStatus";
import { RegularRuns } from "@/components/RegularRuns";
import PaintOrderActions from "@/components/PaintOrderActions";
import { Loader2, Minus, Plus, Users, History, FileDown, FileText, RotateCcw, Paintbrush, Truck } from "lucide-react";
import { toast } from "sonner";

const TODAY = () => new Date().toLocaleDateString("sv-SE");
const isOpen = (o, currentMonth) => !o.cancelled_at && !o.delivered_at && o.month === currentMonth && !(o.driver_id && (o.run_date || TODAY()) <= TODAY());

export function PaintOrdersTable({ orders, onReorder, onChanged, currentMonth }) {
  if (!orders.length) return <p className="text-sm text-muted-foreground" data-testid="paint-no-orders">No orders this month yet.</p>;
  return (
    <Table>
      <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead>Delivery</TableHead><TableHead className="text-right">Total</TableHead>{onReorder && <TableHead />}</TableRow></TableHeader>
      <TableBody>
        {orders.map((o) => (
          <TableRow key={o.id} data-testid={`paint-order-row-${o.id}`} className={o.cancelled_at ? "opacity-60" : ""}>
            <TableCell className="whitespace-nowrap text-xs">{o.created_at.slice(0, 10)}{o.edited_at && <div className="text-[10px] text-muted-foreground">edited</div>}</TableCell>
            <TableCell className="text-sm font-medium">{o.customer.name}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{o.lines.map((l) => `${l.qty}× ${l.label}`).join("; ")}{o.discount > 0 && <span className="text-emerald-700"> · −${o.discount.toFixed(2)} drum disc.</span>}{o.note && <div className="mt-0.5 italic text-kilted-green" data-testid={`paint-order-note-${o.id}`}>Note: {o.note}</div>}</TableCell>
            <TableCell>{o.cancelled_at ? <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700" data-testid={`paint-status-${o.id}`}>Cancelled</span> : <PaintDeliveryStatus order={o} />}</TableCell>
            <TableCell className={`text-right text-sm font-bold ${o.cancelled_at ? "line-through text-muted-foreground" : ""}`}>${o.total.toFixed(2)}</TableCell>
            {onReorder && (
              <TableCell className="text-right">
                {onChanged && isOpen(o, currentMonth) ? <PaintOrderActions order={o} onChanged={onChanged} /> : (
                  <Button size="sm" variant="outline" onClick={() => onReorder(o)} data-testid={`paint-reorder-${o.id}`} className="h-7 gap-1 text-xs"><RotateCcw className="h-3 w-3" /> Reorder</Button>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function statementUrl(month, fmt) {
  return api.get(`/paint/statement?month=${month}&fmt=${fmt}`, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `paint-statement-${month}.${fmt}`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  });
}

export default function PaintOrder() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [quote, setQuote] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/paint/config").then((r) => setCfg(r.data));
    api.get("/paint/customers").then((r) => setCustomers(r.data));
    api.get("/paint/orders").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const items = Object.fromEntries(Object.entries(qty).filter(([, v]) => v > 0));
    if (!Object.keys(items).length) { setQuote(null); return; }
    const t = setTimeout(() => api.post("/paint/quote", { items }).then((r) => setQuote(r.data)).catch(() => setQuote(null)), 200);
    return () => clearTimeout(t);
  }, [qty]);

  const step = (k, d) => setQty((p) => ({ ...p, [k]: Math.max(0, (p[k] || 0) + d) }));

  const placeOrder = async () => {
    if (!customerId) { toast.error("Pick a delivery customer"); return; }
    setBusy(true);
    try {
      await api.post("/paint/orders", { customer_id: customerId, note, items: Object.fromEntries(Object.entries(qty).filter(([, v]) => v > 0)) });
      toast.success("Order placed — added to this month's account");
      setQty({}); setQuote(null); setNote("");
      api.get("/paint/orders").then((r) => setData(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const reorder = async (o) => {
    try { await api.post(`/paint/orders/${o.id}/reorder`); toast.success("Reordered"); api.get("/paint/orders").then((r) => setData(r.data)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!cfg || !data) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold tracking-tight text-kilted-green sm:text-3xl" data-testid="paint-heading"><Paintbrush className="h-6 w-6 text-kilted-gold" /> Paint deliveries</h1>
            <p className="mt-1 text-sm text-muted-foreground">Flat rates Cooma → Jindabyne &amp; Berridale, billed to your monthly account.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => navigate("/book")} data-testid="paint-book-courier-button" className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight"><Truck className="h-3.5 w-3.5" /> Book a courier job</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard?courier=1")} data-testid="paint-courier-jobs-button" className="gap-1.5"><Truck className="h-3.5 w-3.5" /> Courier jobs</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/paint/customers")} data-testid="paint-customers-button" className="gap-1.5"><Users className="h-3.5 w-3.5" /> Customers</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/paint/history")} data-testid="paint-history-button" className="gap-1.5"><History className="h-3.5 w-3.5" /> History</Button>
            <Button variant="outline" size="sm" onClick={() => statementUrl(data.month, "csv")} data-testid="paint-csv-button" className="gap-1.5"><FileDown className="h-3.5 w-3.5" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={() => statementUrl(data.month, "pdf")} data-testid="paint-pdf-button" className="gap-1.5"><FileText className="h-3.5 w-3.5" /> Statement PDF</Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-kilted-gold/50 bg-kilted-gold/10 px-4 py-3" data-testid="paint-month-tally">
          <span className="text-xs font-bold uppercase tracking-wide text-kilted-green">This month</span>
          <span className="text-sm"><b>{data.tally.count}</b> paint orders · ${data.tally.total.toFixed(2)}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-sm" data-testid="paint-month-courier"><b>{data.tally.courier_count ?? 0}</b> courier jobs · ${(data.tally.courier_total ?? 0).toFixed(2)}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-sm font-bold text-kilted-green">${(data.tally.total + (data.tally.courier_total ?? 0)).toFixed(2)} inc GST</span>
        </div>

        <RegularRuns />

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="text-base">New paint order</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Deliver to</p>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger data-testid="paint-customer-select"><SelectValue placeholder="Pick a saved customer" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.address ? ` — ${c.address}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                {cfg.items.map((it) => (
                  <div key={it.key} className="flex items-center gap-3 rounded-md border border-border px-3 py-2" data-testid={`paint-item-${it.key}`}>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{it.label}</span>
                    <span className="text-xs text-muted-foreground">${it.price.toFixed(2)}</span>
                    <div className="flex items-center gap-1.5">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => step(it.key, -1)} data-testid={`paint-minus-${it.key}`}><Minus className="h-3 w-3" /></Button>
                      <span className="w-7 text-center text-sm font-bold" data-testid={`paint-qty-${it.key}`}>{qty[it.key] || 0}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => step(it.key, 1)} data-testid={`paint-plus-${it.key}`}><Plus className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">More than 5 drums (15L + 20L) in one order → $1 off every drum.</p>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Delivery note for the driver <span className="font-normal normal-case">(optional)</span></p>
                <Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2} placeholder="e.g. Leave at the back roller door · call Steve on arrival · colour-matched tins, keep upright" data-testid="paint-note-input" />
                <p className="text-[11px] text-muted-foreground">Shows on the driver's run sheet for this delivery.</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-kilted-green/20">
            <CardHeader><CardTitle className="text-base">Order total</CardTitle></CardHeader>
            <CardContent>
              {quote ? (
                <div className="space-y-1.5 text-sm" data-testid="paint-quote">
                  <div className="flex justify-between"><span>Subtotal</span><span>${quote.subtotal.toFixed(2)}</span></div>
                  {quote.discount > 0 && <div className="flex justify-between text-emerald-700" data-testid="paint-drum-discount"><span>Drum discount ({quote.drums} drums)</span><span>−${quote.discount.toFixed(2)}</span></div>}
                  <div className="flex justify-between"><span>GST</span><span>${quote.gst.toFixed(2)}</span></div>
                  <div className="mt-1 flex justify-between rounded-md bg-kilted-green px-3 py-2 font-bold text-white"><span>Total</span><span data-testid="paint-total">${quote.total.toFixed(2)}</span></div>
                </div>
              ) : <p className="text-sm text-muted-foreground">Add items to see the price.</p>}
              <Button className="mt-4 w-full bg-kilted-gold font-bold text-kilted-green hover:bg-kilted-goldLight" disabled={!quote || busy} onClick={placeOrder} data-testid="paint-place-order">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Place order (on account)"}
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader><CardTitle className="text-base">This month's orders</CardTitle></CardHeader>
          <CardContent><PaintOrdersTable orders={data.orders} onReorder={reorder} onChanged={load} currentMonth={data.current_month} /></CardContent>
        </Card>
      </main>
    </div>
  );
}
